import {
	afterNextRender,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	HostListener,
	Injector,
	OnDestroy,
	OnInit,
	ViewChild,
} from "@angular/core";
import { MAT_DATE_FORMATS } from "@angular/material/core";
import { MatLegacyDialog as MatDialog } from "@angular/material/legacy-dialog";
import { createRxValueWithCache } from "@app/_helpers/rx-value";
import { createRxValue, createScheduleDayParserFromUser } from "@app/_helpers/utils";
import { ClockService } from "@app/_services/clock.service";
import { FadeInOut, SlideUpDown, SlideUpDownBump } from "@app/animations/fade";
import {
	ComeAndGoAbsenceDialogComponent,
	WorkingHoursAbsenceData,
} from "@app/components/come-and-go-absence-dialog/come-and-go-absence-dialog.component";
import {
	ComeAndGoCreateDialogComponent,
	WorkingHoursCreateData,
} from "@app/components/come-and-go-create-dialog/come-and-go-create-dialog.component";
import {
	ComeAndGoMultiUpdateDialogComponent,
	WorkingHoursUpdateData,
} from "@app/components/come-and-go-multi-update-dialog/come-and-go-update-dialog.component";
import { MixedTimesSyncService } from "@app/services/mixed-times-sync.service";
import {
	TimeTrackCreateData,
	TimeTrackerCalendarCreateDialogComponent,
} from "@app/shared/time-tracker-calendar-create-dialog/time-tracker-calendar-create-dialog.component";
import { TimeTrackerCalendarStepperCreateDialogComponent } from "@app/shared/time-tracker-calendar-stepper-create-dialog/time-tracker-calendar-stepper-create-dialog.component";
import { TimeTrackerCalendarUpdateDialogComponent } from "@app/shared/time-tracker-calendar-update-dialog/time-tracker-calendar-update-dialog.component";
import defaults from "@env/defaults";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import {
	ComegoQuery,
	ComegoTime,
	FeedEntry,
	MyTimesQuery,
	MyTimesService,
	ProjectsQuery,
	ProjectsService,
	Time,
	UserSettingsQuery,
} from "@timeghost/web-api";
import { CalendarEvent } from "calendar-utils";
import {
	addDays,
	addMinutes,
	differenceInSeconds,
	getISODay,
	isSameDay,
	startOfDay,
	startOfMinute,
	startOfISOWeek as startOfWeek,
} from "date-fns/esm";
import omit from "lodash-es/omit";
import { MediaObserver } from "ngx-flexible-layout";
import { combineLatest, firstValueFrom } from "rxjs";
import { distinctUntilChanged, filter, finalize, map, shareReplay, take } from "rxjs/operators";
import { TimerService } from "../timer-calendar-view.service";
import {
	CalendarTimerDaySegmentsComponent,
	ProjectFillClickEvent,
	ProjectPointerDownEvent,
	ProjectPointerMoveEvent,
	WorkingFillClickEvent,
	WorkingPointerDownEvent,
} from "./calendar-timer-day-segments/calendar-timer-day-segments.component";
import { getCurrentTimeLeftPx } from "./current-time-indicator/current-time-indicator.component";
import { resolveProjecthourFillFromDomIndex } from "./timer-calendar-segment.builder";
import {
	buildCalendarViewModel,
	COMPACT_DAY_ROW_BASE_HEIGHT_PX,
	getScheduleHourBoundsForRange,
	resolveSegmentIndexFromTime,
} from "./timer-calendar-view.mapper";
import {
	CalendarDayViewModel,
	CalendarViewModel,
	HoursSegmentPresets,
	ScheduleTime,
	SegmentRangeFill,
} from "./timer-calendar-view.models";

export const TIMEFORMAT = {
	parse: { dateInput: "H:mm:ss" },
	display: {
		dateInput: "H:mm:ss",
		monthYearLabel: "MMM YYYY",
		dateA11yLabel: "LL",
		monthYearA11yLabel: "MMMM YYYY",
	},
};

const EMPTY_SELECTION = new Set<number>();
const EMPTY_PROJECT_SELECTION = new Map<number, Set<number>>();
const CALENDAR_GRID_HEADER_PX = 56;

@UntilDestroy()
@Component({
	selector: "app-calendar-view",
	templateUrl: "./timer-calendar-view.component.html",
	styleUrls: ["./timer-calendar-view.component.scss"],
	animations: [FadeInOut, SlideUpDown, SlideUpDownBump],
	providers: [{ provide: MAT_DATE_FORMATS, useValue: TIMEFORMAT }],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CalendarTimerComponent implements OnInit, OnDestroy {
	@ViewChild("timelineScroll") timelineScroll?: ElementRef<HTMLElement>;
	@ViewChild("calendarGrid") calendarGrid?: ElementRef<HTMLElement>;
	@ViewChild("dayRowMeasure") dayRowMeasure?: ElementRef<HTMLElement>;

	measuredDayRowHeightPx = 0;
	private resizeObserver?: ResizeObserver;

	readonly rangeValue$ = this.service.range.asObservable();
	readonly viewDate$ = this.service.viewDate$;
	readonly viewDate$status = this.service.viewDate$status;
	readonly range = this.service.range;
	readonly maxRangeStart = this.service.maxRangeStart;
	readonly minRangeStart = this.service.minRangeStart;
	readonly isLoading$ = this.service.isLoading$;
	readonly projecthoursEnabled = this.service.projecthoursEnabled;
	readonly workinghoursEnabled = this.service.workinghoursEnabled;
	readonly selectedViewMode = createRxValueWithCache("selectedViewMode", "calendar");
	viewChange(view: "list" | "calendar") {
		this.selectedViewMode.update((v) => view);
	}
	readonly schedule$ = this.service.schedule$;

	private readonly hoursSegmentPresetSteps: HoursSegmentPresets[] = [2, 4, 6, 12];
	private readonly segmentWidthPresetsSteps: number[] = [65, 55, 40, 35];
	readonly hoursSegmentPreserts = createRxValueWithCache<HoursSegmentPresets>("timerCalendarView", 2);
	readonly startFromWorkDay = createRxValueWithCache<boolean>("startFromWorkDay", false);
	readonly startFromSchedule = createRxValueWithCache<boolean>("startSchedule", false);
	readonly activeDaysOfWeek = createRxValueWithCache<boolean[]>("activeDaysOfWeek", [
		true,
		true,
		true,
		true,
		true,
		true,
		true,
	]);
	readonly weekDaysForMenu = Array.from({ length: 7 }, (_, index) => ({
		index,
		date: addDays(startOfWeek(new Date()), index),
	}));
	readonly segmentWidthPx = createRxValue(55);
	private readonly layoutRevision = createRxValue(0);

	private selectedWorkinghoursByDay = new Map<number, Set<number>>();
	private activeDragDayKey: number | null = null;
	private dragStartIndexWorkinghours: number | null = null;
	private isDraggingWorkinghours = false;

	private selectedProjecthoursByDay = new Map<number, Map<number, Set<number>>>();
	private activeDragProjectDayKey: number | null = null;
	private activeDragProjectRowIndex: number | null = null;
	private dragStartIndexProjecthours: number | null = null;
	private isDraggingProjecthours = false;
	private draggingTimeEntry: Time | null = null;
	private draggingComegoEntry: ComegoTime | null = null;
	private projectDragDidMove = false;
	private projectDragPointerDownTarget: EventTarget | null = null;
	private projectDragPointerDownSegIndex: number | null = null;
	private activeDragDayViewModel: CalendarDayViewModel | null = null;
	private workingDragDidMove = false;
	private workingDragPointerDownFill: SegmentRangeFill | null = null;
	private activeDragWorkingDayViewModel: CalendarDayViewModel | null = null;

	private projectRowsCollapsed = new Map<number, boolean>();

	readonly weeklyTimes$ = this.service.times$;
	readonly visibleWeeklyTimes$ = combineLatest([this.weeklyTimes$, this.activeDaysOfWeek.asObservable()]).pipe(
		map(([days, active]) => days?.filter((day) => active[getISODay(day.date) - 1]) ?? days)
	);

	private readonly compactDayRows$ = this.media.asObservable().pipe(
		map((changes) => !changes.some((c) => ["lg", "xl"].includes(c.mqAlias))),
		distinctUntilChanged(),
		shareReplay(1)
	);

	readonly calendarView$ = combineLatest([
		this.visibleWeeklyTimes$,
		this.hoursSegmentPreserts.asObservable(),
		this.startFromWorkDay.asObservable(),
		this.startFromSchedule.asObservable(),
		this.segmentWidthPx.asObservable(),
		this.service.range.asObservable(),
		this.layoutRevision.asObservable(),
		this.projecthoursEnabled.value$,
		this.workinghoursEnabled.value$,
		this.compactDayRows$,
	]).pipe(
		map(
			([
				days,
				preset,
				fromWorkDay,
				fromSchedule,
				segWidth,
				range,
				_layoutRev,
				projecthoursEnabled,
				workinghoursEnabled,
				compactDayRows,
			]) => {
				const scheduleHourBounds = fromSchedule
					? getScheduleHourBoundsForRange(this.userSettingsQuery, range?.start)
					: null;
				return buildCalendarViewModel(
					days ?? [],
					{
						preset,
						fromWorkDay,
						fromSchedule,
						segmentWidthPx: segWidth,
						scheduleHourBounds,
						dayRowBaseHeightPx: compactDayRows ? COMPACT_DAY_ROW_BASE_HEIGHT_PX : undefined,
					},
					{ projecthoursEnabled: !!projecthoursEnabled, workinghoursEnabled: !!workinghoursEnabled },
					this.projectRowsCollapsed,
					(id) => this.getProjectColor(id)
				);
			}
		),
		shareReplay(1)
	);

	constructor(
		private media: MediaObserver,
		private userSettingsQuery: UserSettingsQuery,
		private comegoQuery: ComegoQuery,
		private service: TimerService,
		private clock: ClockService,
		private injector: Injector,
		private dialog: MatDialog,
		private projectQuery: ProjectsQuery,
		private projectsService: ProjectsService,
		private myTimesQuery: MyTimesQuery,
		private myTimesService: MyTimesService,
		private cdr: ChangeDetectorRef,
		private mixedTimesSyncService: MixedTimesSyncService
	) {}

	ngOnInit() {
		this.syncSegmentWidthToHoursPreset();
		this.syncActiveDaysFromSchedule();
		this.service.rangeFetch$.pipe(untilDestroyed(this)).subscribe();
		this.weeklyTimes$.pipe(filter(Boolean), take(1), untilDestroyed(this)).subscribe(() => {
			afterNextRender(() => this.scrollToCurrentTime(), { injector: this.injector });
		});
		this.calendarView$.pipe(untilDestroyed(this)).subscribe(() => {
			afterNextRender(
				() => {
					this.setupCalendarGridResizeObserver();
					this.updateMeasuredDayRowHeight();
				},
				{ injector: this.injector }
			);
		});
		afterNextRender(() => this.setupCalendarGridResizeObserver(), { injector: this.injector });
	}

	ngOnDestroy(): void {
		this.resizeObserver?.disconnect();
	}

	prev() {
		return this.service.prev();
	}
	today() {
		return this.service.today();
	}
	next() {
		return this.service.next();
	}

	workingSelectionFor(dayKey: number): Set<number> {
		return this.selectedWorkinghoursByDay.get(dayKey) ?? EMPTY_SELECTION;
	}

	projectSelectionFor(dayKey: number): Map<number, Set<number>> {
		return this.selectedProjecthoursByDay.get(dayKey) ?? EMPTY_PROJECT_SELECTION;
	}

	get draggingComegoSelectionStyle(): Record<string, string> | undefined {
		if (!this.draggingComegoEntry) return undefined;
		const typeClass = this.comegoTypeToSegmentClass(this.draggingComegoEntry.type);
		if (!typeClass) return undefined;
		return this.getWorkinghourTypeSelectionStyle(typeClass) ?? undefined;
	}

	get draggingTimeEntryProjectColor(): string | undefined {
		if (!this.draggingTimeEntry) return undefined;
		return this.getProjectColor(this.draggingTimeEntry.project?.id) ?? "#94a3b8";
	}

	onProjectRowToggle(day: CalendarDayViewModel): void {
		this.projectRowsCollapsed.set(day.dayKey, !(this.projectRowsCollapsed.get(day.dayKey) ?? false));
		this.layoutRevision.update((v) => v + 1);
		this.cdr.markForCheck();
	}

	onProjectPointerDown(day: CalendarDayViewModel, payload: ProjectPointerDownEvent): void {
		this.startDragProjecthours(day, payload.rowIndex, payload.segIndex, payload.event, payload.time);
	}

	onProjectPointerMove(day: CalendarDayViewModel, payload: ProjectPointerMoveEvent): void {
		this.onProjecthourRowPointerMove(day, payload.rowIndex, payload.event);
	}

	onProjectFillClick(day: CalendarDayViewModel, payload: ProjectFillClickEvent): void {
		console.log("Timer-calendar: onProjectFillClick", day, payload);
		/* if (payload.timeRef) {
			this.openProjectTimeSegment(payload.timeRef);
		} else  */
		if (payload.feedRef) {
			this.openFeedEventSegement(payload.feedRef, day.date);
		}
	}

	onProjectFillContextEdit(time: Time): void {
		this.openProjectTimeSegment(time);
	}

	async onProjectFillContextDuplicate(time: Time): Promise<void> {
		if (time.project?.completed) return;
		const entity = this.myTimesQuery.getEntity(time.id);
		if (!entity?.id) return;

		const project = (time.project?.id && (await this.projectsService.getById(time.project.id))) || null;

		const payload = {
			billable: time.billable,
			name: time.name,
			// @ts-ignore
			task: time.task,
			...(!project || project.completed || project["deleted"]
				? {
						project: this.projectQuery.getAll({ filterBy: (x) => x.useAsDefault })[0],
						task: null,
					}
				: {
						project: time.project,
						task: time.task,
					}),
			start: new Date(time.start),
			end: new Date(time.end),
			tags: time.tags as any,
		};
		const confirmed = await this.mixedTimesSyncService.confirmProjectTimeCreate(payload);
		if (!confirmed) return;

		const created = await firstValueFrom(this.myTimesService.add(payload));
		const entry = (Array.isArray(created) ? created[0] : created) as Time | undefined;
		if (entry) {
			await this.mixedTimesSyncService.finalizeAfterProjectTimeCreate(entry);
		}
		this.layoutRevision.update((v) => v + 1);
		this.cdr.markForCheck();
	}

	onProjectFillContextRemove(time: Time): void {
		if (time.project?.completed) return;
		const entity = this.myTimesQuery.getEntity(time.id);
		if (!entity?.id) return;

		this.myTimesService
			.delete(entity)
			.pipe(finalize(() => this.cdr.markForCheck()))
			.subscribe(() => {
				this.layoutRevision.update((v) => v + 1);
				this.mixedTimesSyncService.handleProjectTimeDelete(time);
			});
	}

	onWorkingPointerDown(day: CalendarDayViewModel, payload: WorkingPointerDownEvent): void {
		this.startDragWorkinghours(day, payload.segIndex, payload.event, payload.comegoRef, payload.fill);
	}

	onWorkingPointerMove(day: CalendarDayViewModel, event: PointerEvent): void {
		this.onWorkinghourRowPointerMove(day, event);
	}

	onWorkingFillClick(day: CalendarDayViewModel, payload: WorkingFillClickEvent): void {
		this.onWorkinghourFillClick(day.date, payload.fill);
	}

	startDragWorkinghours(
		day: CalendarDayViewModel,
		index: number,
		event: PointerEvent,
		comegoEntry?: ComegoTime,
		fill?: SegmentRangeFill
	): void {
		this.draggingComegoEntry = comegoEntry ?? null;
		this.isDraggingWorkinghours = true;
		this.activeDragDayKey = day.dayKey;
		this.activeDragWorkingDayViewModel = day;
		this.dragStartIndexWorkinghours = index;
		this.workingDragDidMove = false;
		this.workingDragPointerDownFill = fill ?? null;
		this.captureDragPointer(event);
		this.clearOtherDaySelections(day.dayKey);
		this.selectRangeWorkinghours(day.dayKey, index, index);
	}

	startDragProjecthours(
		day: CalendarDayViewModel,
		rowIndex: number,
		index: number,
		event: PointerEvent,
		time?: Time
	): void {
		console.log("Timer-calendar: startDragProjecthours", day, rowIndex, index, event, time);
		this.draggingTimeEntry = time ?? null;
		this.isDraggingProjecthours = true;
		this.activeDragProjectDayKey = day.dayKey;
		this.activeDragProjectRowIndex = rowIndex;
		this.activeDragDayViewModel = day;
		this.dragStartIndexProjecthours = index;
		this.projectDragDidMove = false;
		this.projectDragPointerDownTarget = event.target;
		this.projectDragPointerDownSegIndex = index;
		this.captureDragPointer(event);
		this.clearOtherProjectDaySelections(day.dayKey);
		this.selectedProjecthoursByDay.delete(day.dayKey);
		this.selectRangeProjecthours(day.dayKey, rowIndex, index, index);
	}

	onWorkinghourRowPointerMove(day: CalendarDayViewModel, event: PointerEvent): void {
		if (
			!this.isDraggingWorkinghours ||
			this.activeDragDayKey !== day.dayKey ||
			this.dragStartIndexWorkinghours === null
		) {
			return;
		}
		const index = this.resolveSegmentIndexFromPointer(event.currentTarget as HTMLElement, event.clientX, day);
		this.dragOverWorkinghours(day.dayKey, index, day);
	}

	onProjecthourRowPointerMove(day: CalendarDayViewModel, rowIndex: number, event: PointerEvent): void {
		if (
			!this.isDraggingProjecthours ||
			this.activeDragProjectDayKey !== day.dayKey ||
			this.activeDragProjectRowIndex !== rowIndex ||
			this.dragStartIndexProjecthours === null
		) {
			return;
		}
		const index = this.resolveSegmentIndexFromPointer(event.currentTarget as HTMLElement, event.clientX, day);
		this.dragOverProjecthours(day.dayKey, rowIndex, index);
	}

	dragOverWorkinghours(dayKey: number, index: number, day: CalendarDayViewModel): void {
		if (
			!this.isDraggingWorkinghours ||
			this.activeDragDayKey !== dayKey ||
			this.dragStartIndexWorkinghours === null
		) {
			return;
		}
		if (!day.workingSegments[index]?.isEmpty && !this.draggingComegoEntry) return;
		if (index !== this.dragStartIndexWorkinghours) {
			this.workingDragDidMove = true;
		}
		this.selectRangeWorkinghours(dayKey, this.dragStartIndexWorkinghours, index);
	}

	dragOverProjecthours(dayKey: number, rowIndex: number, index: number): void {
		if (
			!this.isDraggingProjecthours ||
			this.activeDragProjectDayKey !== dayKey ||
			this.activeDragProjectRowIndex !== rowIndex ||
			this.dragStartIndexProjecthours === null
		) {
			return;
		}
		if (index !== this.dragStartIndexProjecthours) {
			this.projectDragDidMove = true;
		}
		this.selectRangeProjecthours(dayKey, rowIndex, this.dragStartIndexProjecthours, index);
	}

	endDragWorkinghours(date: Date): void {
		const dayKey = date.getTime();
		const segments = Array.from(this.selectedWorkinghoursByDay.get(dayKey) ?? []);
		const didMove = this.workingDragDidMove;
		const fill = this.workingDragPointerDownFill;

		this.isDraggingWorkinghours = false;
		this.activeDragDayKey = null;
		this.activeDragWorkingDayViewModel = null;
		this.dragStartIndexWorkinghours = null;
		this.workingDragDidMove = false;
		this.workingDragPointerDownFill = null;
		if (!didMove && fill) {
			this.onWorkinghourFillClick(date, fill);
			this.selectedWorkinghoursByDay.delete(dayKey);
			this.cdr.markForCheck();
			return;
		}

		this.createWorkinghourTime(date, segments);
		this.cdr.markForCheck();
	}

	endDragProjecthours(date: Date): void {
		const dayKey = date.getTime();
		const rowIndex = this.activeDragProjectRowIndex ?? 0;
		const segments = Array.from(this.selectedProjecthoursByDay.get(dayKey)?.get(rowIndex) ?? []);
		const day = this.activeDragDayViewModel;
		const didMove = this.projectDragDidMove;
		const pointerTarget = this.projectDragPointerDownTarget;
		const pointerDownSegIndex = this.projectDragPointerDownSegIndex;

		this.isDraggingProjecthours = false;
		this.activeDragProjectDayKey = null;
		this.activeDragProjectRowIndex = null;
		this.activeDragDayViewModel = null;
		this.dragStartIndexProjecthours = null;
		this.projectDragDidMove = false;
		this.projectDragPointerDownTarget = null;
		this.projectDragPointerDownSegIndex = null;

		if (!didMove && day && pointerDownSegIndex !== null) {
			const fillEl = pointerTarget instanceof Element ? pointerTarget.closest(".segment-range-fill") : null;
			const fillIndexAttr = fillEl?.getAttribute("data-fill-index");
			if (fillIndexAttr !== null && fillIndexAttr !== undefined) {
				const layer = resolveProjecthourFillFromDomIndex(day.projectRows, rowIndex, Number(fillIndexAttr));
				if (layer?.timeRef && !this.draggingTimeEntry) {
					this.openProjectTimeSegment(layer.timeRef);
					this.selectedProjecthoursByDay.delete(dayKey);
					this.cdr.markForCheck();
					return;
				} else if (layer?.timeRef && this.draggingTimeEntry) {
					this.selectedProjecthoursByDay.delete(dayKey);
					this.cdr.markForCheck();
					this.openProjectTimeSegment(this.draggingTimeEntry);
					return;
				}
				if (layer?.feedRef) {
					this.openFeedEventSegement(layer.feedRef, day.date);
					this.selectedProjecthoursByDay.delete(dayKey);
					this.cdr.markForCheck();
					return;
				}
			}
		}

		this.createProjecthourTime(date, segments);
		this.cdr.markForCheck();
	}

	@HostListener("document:pointerup")
	onDocumentPointerUp(): void {
		if (this.isDraggingWorkinghours && this.activeDragDayKey !== null) {
			this.endDragWorkinghours(new Date(this.activeDragDayKey));
			return;
		}
		if (this.isDraggingProjecthours && this.activeDragProjectDayKey !== null) {
			this.endDragProjecthours(new Date(this.activeDragProjectDayKey));
		}
	}

	private clearOtherDaySelections(activeDayKey: number): void {
		for (const dayKey of this.selectedWorkinghoursByDay.keys()) {
			if (dayKey !== activeDayKey) {
				this.selectedWorkinghoursByDay.delete(dayKey);
			}
		}
	}

	private clearOtherProjectDaySelections(activeDayKey: number): void {
		for (const dayKey of this.selectedProjecthoursByDay.keys()) {
			if (dayKey !== activeDayKey) {
				this.selectedProjecthoursByDay.delete(dayKey);
			}
		}
	}

	private selectRangeWorkinghours(dayKey: number, start: number, end: number): void {
		const from = Math.min(start, end);
		const to = Math.max(start, end);
		const selection = new Set<number>();
		for (let i = from; i <= to; i++) {
			selection.add(i);
		}
		this.selectedWorkinghoursByDay.set(dayKey, selection);
		this.markDayDirty(dayKey);
	}

	private selectRangeProjecthours(dayKey: number, rowIndex: number, start: number, end: number): void {
		const from = Math.min(start, end);
		const to = Math.max(start, end);
		const selection = new Set<number>();
		for (let i = from; i <= to; i++) {
			selection.add(i);
		}
		const daySelection = new Map(this.selectedProjecthoursByDay.get(dayKey) ?? []);
		daySelection.set(rowIndex, selection);
		this.selectedProjecthoursByDay.set(dayKey, daySelection);
		this.markDayDirty(dayKey);
	}

	private markDayDirty(_dayKey: number): void {
		this.cdr.markForCheck();
	}

	scrollToCurrentTime(): void {
		const el = this.timelineScroll?.nativeElement;
		if (!el) return;
		el.scrollLeft = this.getCurrentTimeScrollLeft(el);
	}

	gridTemplateRows(vm: CalendarViewModel): string {
		const count = Math.max(vm.days.length, 1);
		return `56px repeat(${count}, minmax(${vm.dayRowHeightPx}px, 1fr))`;
	}

	private setupCalendarGridResizeObserver(): void {
		const el = this.calendarGrid?.nativeElement;
		if (!el) return;

		if (this.resizeObserver) {
			this.updateMeasuredDayRowHeight();
			return;
		}

		this.resizeObserver = new ResizeObserver(() => this.updateMeasuredDayRowHeight());
		this.resizeObserver.observe(el);
		this.updateMeasuredDayRowHeight();
	}

	private updateMeasuredDayRowHeight(): void {
		const vm = this.getLatestViewModel();
		const minHeight = vm?.dayRowHeightPx ?? 0;
		const measuredEl = this.dayRowMeasure?.nativeElement;
		const rowHeight = measuredEl?.clientHeight ? measuredEl.clientHeight : this.getCalculatedDayRowHeight();
		const next = Math.max(minHeight, Math.round(rowHeight));

		if (next === this.measuredDayRowHeightPx) return;

		this.measuredDayRowHeightPx = next;
		this.cdr.markForCheck();
		afterNextRender(() => this.scrollToCurrentTime(), { injector: this.injector });
	}

	private getCalculatedDayRowHeight(): number {
		const el = this.calendarGrid?.nativeElement;
		if (!el) return 0;

		const vm = this.getLatestViewModel();
		const visibleDayCount = Math.max(vm?.visibleDayCount ?? 1, 1);
		return (el.clientHeight - CALENDAR_GRID_HEADER_PX) / visibleDayCount;
	}

	private getCurrentTimeScrollLeft(container: HTMLElement): number {
		const vm = this.getLatestViewModel();
		if (!vm) return 0;
		const now = this.clock.now();
		const contentX = getCurrentTimeLeftPx(now, {
			dayStartHour: vm.dayStartHour,
			dayEndHour: vm.dayEndHour,
			segmentWidthPx: vm.segmentWidthPx,
			timelineWidthPx: vm.timelineWidthPx,
		});
		const target = contentX - container.clientWidth / 2;
		const maxScroll = container.scrollWidth - container.clientWidth;
		return Math.max(0, Math.min(maxScroll, target));
	}

	private getLatestViewModel(): CalendarViewModel | null {
		let vm: CalendarViewModel | null = null;
		this.calendarView$.pipe(take(1)).subscribe((v) => (vm = v));
		return vm;
	}

	zoomOut() {
		const index = this.hoursSegmentPresetSteps.indexOf(this.hoursSegmentPreserts.value);
		this.setHoursSegmentPresetIndex(index <= 0 ? 0 : index - 1);
	}

	zoomIn() {
		const index = this.hoursSegmentPresetSteps.indexOf(this.hoursSegmentPreserts.value);
		this.setHoursSegmentPresetIndex(
			index < 0 || index >= this.hoursSegmentPresetSteps.length - 1
				? this.hoursSegmentPresetSteps.length - 1
				: index + 1
		);
	}

	private syncSegmentWidthToHoursPreset(): void {
		const index = this.hoursSegmentPresetSteps.indexOf(this.hoursSegmentPreserts.value);
		this.segmentWidthPx.update(() => this.segmentWidthPresetsSteps[index < 0 ? 0 : index]);
	}

	private setHoursSegmentPresetIndex(index: number): void {
		const safeIndex = Math.max(0, Math.min(this.hoursSegmentPresetSteps.length - 1, index));
		this.hoursSegmentPreserts.update(() => this.hoursSegmentPresetSteps[safeIndex]);
		this.segmentWidthPx.update(() => this.segmentWidthPresetsSteps[safeIndex]);
		this.layoutRevision.update((v) => v + 1);
		afterNextRender(() => this.scrollToCurrentTime(), { injector: this.injector });
		this.cdr.markForCheck();
	}

	onViewBoundsToggleChange(): void {
		this.syncActiveDaysFromSchedule();
		this.layoutRevision.update((v) => v + 1);
		afterNextRender(() => this.scrollToCurrentTime(), { injector: this.injector });
		this.cdr.markForCheck();
	}

	private syncActiveDaysFromSchedule(): void {
		if (!this.startFromSchedule.value) return;
		const user = this.userSettingsQuery.getValue();
		const next = [...this.activeDaysOfWeek.value];
		for (const weekDay of this.weekDaysForMenu) {
			const schedule = createScheduleDayParserFromUser(user, weekDay.date, undefined, true)?.([], weekDay.date);
			if (schedule?.cap === 0) {
				next[weekDay.index] = false;
			}
		}
		if (!next.some(Boolean)) return;
		this.activeDaysOfWeek.update(next);
	}

	onWeekDayVisibilityChange(index: number, visible: boolean): void {
		const next = [...this.activeDaysOfWeek.value];
		next[index] = visible;
		if (!next.some(Boolean)) return;
		this.activeDaysOfWeek.update(next);
		this.cdr.markForCheck();
	}

	createNewWorkinghourTime(date: Date) {
		this.dialog.open(ComeAndGoCreateDialogComponent, { data: { date } as WorkingHoursCreateData });
	}

	createNewAbsenceTime(date: Date) {
		this.dialog.open(ComeAndGoAbsenceDialogComponent, { data: { initialDate: date } as WorkingHoursAbsenceData });
	}

	createNewProjecthourTime(date: Date) {
		this.dialog.open(TimeTrackerCalendarCreateDialogComponent, {
			data: { date } as TimeTrackCreateData,
		});
	}

	createWorkinghourTime(date: Date, segments: number[]) {
		if (!segments.length) return;
		this.selectedWorkinghoursByDay.delete(date.getTime());
		const vm = this.getLatestViewModel();
		if (!vm) return;
		const startIndex = segments[0];
		const start = this.resolveIndexStartTime(date, startIndex, vm);
		const end = this.resolveIndexEndTime(date, segments[segments.length - 1], vm);
		const comegoEntry = this.draggingComegoEntry;
		this.draggingComegoEntry = null;

		if (comegoEntry) {
			const comegoEntryStartSegment = resolveSegmentIndexFromTime(
				date,
				new Date(comegoEntry.start),
				vm.dayStartHour,
				vm.dayEndHour,
				vm.segmentsPerHour
			);
			let timeStart = new Date(comegoEntry.start);
			if (comegoEntryStartSegment !== startIndex) {
				timeStart = new Date(comegoEntry.start) < start ? new Date(comegoEntry.start) : start;
			}
			const timeEnd = new Date(comegoEntry.end) > end ? new Date(comegoEntry.end) : end;
			if (
				new Date(comegoEntry.start).getTime() === timeStart.getTime() &&
				new Date(comegoEntry.end).getTime() === timeEnd.getTime()
			) {
				return;
			}
			this.dialog.open(ComeAndGoMultiUpdateDialogComponent, {
				data: this.buildComegoDayUpdateData(date, comegoEntry, timeStart, timeEnd),
			});
		} else if (!comegoEntry) {
			this.dialog.open(ComeAndGoCreateDialogComponent, {
				data: {
					initialTimes: [{ start, end, type: "work" }],
					date,
				} as WorkingHoursCreateData,
			});
		}
	}

	openSegmentDay(date: Date) {
		this.dialog.open(ComeAndGoMultiUpdateDialogComponent, {
			data: {
				time: { value: [], date, refetchFromDate: true },
				forceRecreateDay: true,
			} as WorkingHoursUpdateData,
		});
	}

	private buildComegoDayUpdateData(
		date: Date,
		comegoEntry: ComegoTime,
		timeStart: Date,
		timeEnd: Date
	): WorkingHoursUpdateData {
		const userId = this.userSettingsQuery.getValue().id;
		const dayEntries = this.comegoQuery.getAll({
			filterBy: (x) => x.user?.id === userId && isSameDay(new Date(x.start), date),
		});
		const updatedStart = timeStart.toISOString();
		const updatedEnd = timeEnd.toISOString();
		const value = dayEntries.map((entry) => {
			const base = entry.id === comegoEntry.id ? { ...entry, start: updatedStart, end: updatedEnd } : entry;
			return omit(base, "meta", "timeDiff");
		});

		return {
			time: { value, date, refetchFromDate: false },
			forceRecreateDay: true,
			updateValidation: true,
		};
	}

	createProjecthourTime(date: Date, segments: number[]) {
		if (!segments.length) return;
		this.selectedProjecthoursByDay.delete(date.getTime());
		const vm = this.getLatestViewModel();
		if (!vm) return;
		const startIndex = segments[0];
		const start = this.resolveIndexStartTime(date, startIndex, vm);
		const end = this.resolveIndexEndTime(date, segments[segments.length - 1], vm);
		const timeEntry = this.draggingTimeEntry;
		this.draggingTimeEntry = null;

		if (timeEntry) {
			const timeEntryStartSegment = resolveSegmentIndexFromTime(
				date,
				new Date(timeEntry.start),
				vm.dayStartHour,
				vm.dayEndHour,
				vm.segmentsPerHour
			);
			let timeStart = new Date(timeEntry.start);
			if (timeEntryStartSegment !== startIndex) {
				timeStart = new Date(timeEntry.start) < start ? new Date(timeEntry.start) : start;
			}
			const timeEnd = new Date(timeEntry.end) > end ? new Date(timeEntry.end) : end;
			if (
				new Date(timeEntry.start).getTime() === timeStart.getTime() &&
				new Date(timeEntry.end).getTime() === timeEnd.getTime()
			) {
				this.openProjectTimeSegment(timeEntry);
				return;
			}
			this.dialog.open(TimeTrackerCalendarUpdateDialogComponent, {
				data: {
					title: timeEntry.name,
					start: timeStart,
					end: timeEnd,
					meta: { time: timeEntry, oldStart: new Date(timeEntry.start), oldEnd: new Date(timeEntry.end) },
				} as CalendarEvent,
			});
		} else if (!timeEntry) {
			this.dialog.open(TimeTrackerCalendarCreateDialogComponent, {
				data: { date, start, end, tags: [] } as TimeTrackCreateData,
			});
		}
	}

	openProjectTimeSegment(timeEntry: Time) {
		this.dialog.open(TimeTrackerCalendarUpdateDialogComponent, {
			data: {
				title: timeEntry.name,
				start: new Date(timeEntry.start),
				end: new Date(timeEntry.end),
				meta: { time: timeEntry },
			} as CalendarEvent,
		});
	}

	openFeedEventSegement(feed: FeedEntry, date: Date) {
		const start = new Date(feed.start);
		const end = ((dt: Date) =>
			differenceInSeconds(dt, start) <= 0 ? addMinutes(start.getTime(), defaults.MIN_DURATION_MINUTES) : dt)(
			new Date(feed.end || feed.start)
		);
		this.dialog.open(TimeTrackerCalendarStepperCreateDialogComponent, {
			data: {
				date,
				start: startOfMinute(start),
				end: startOfMinute(end),
				project: null,
				title: feed.name,
				outlookRefId: feed.id,
				tags: [],
				lang: { submit: "feed.entry.save" },
			} as TimeTrackCreateData,
		});
	}

	onWorkinghourFillClick(date: Date, fill: SegmentRangeFill): void {
		if (fill.isSuggestion) {
			if (fill.scheduleRef) {
				this.createScheduleSuggestion(date, fill.scheduleRef);
			}
			return;
		}
		this.openSegmentDay(date);
	}

	createScheduleSuggestion(date: Date, suggestion: ScheduleTime): void {
		this.dialog.open(ComeAndGoCreateDialogComponent, {
			data: {
				initialTimes: [{ start: suggestion.start, end: suggestion.end, type: suggestion.type }],
				date,
			} as WorkingHoursCreateData,
		});
	}

	private resolveIndexStartTime(date: Date, index: number, vm: CalendarViewModel): Date {
		const segmentDuration = 60 / vm.segmentsPerHour;
		const dayStart = startOfDay(date);
		return addMinutes(dayStart, vm.dayStartHour * 60 + index * segmentDuration);
	}

	private resolveIndexEndTime(date: Date, index: number, vm: CalendarViewModel): Date {
		const segmentDuration = 60 / vm.segmentsPerHour;
		const dayStart = startOfDay(date);
		return addMinutes(dayStart, vm.dayStartHour * 60 + (index + 1) * segmentDuration);
	}

	private resolveSegmentIndexFromPointer(
		rowElement: HTMLElement,
		clientX: number,
		day: CalendarDayViewModel
	): number {
		const vm = this.getLatestViewModel();
		const segWidth = vm?.segmentWidthPx ?? 55;
		const total = day.workingSegments.length;
		const x = clientX - rowElement.getBoundingClientRect().left;
		return Math.max(0, Math.min(total - 1, Math.floor(x / segWidth)));
	}

	private captureDragPointer(event: PointerEvent): void {
		const rowTrack = (event.currentTarget as HTMLElement).parentElement;
		rowTrack?.setPointerCapture(event.pointerId);
	}

	private getProjectColor(projectId?: string): string | undefined {
		if (!projectId) return undefined;
		return this.projectQuery.getEntity(projectId)?.color ?? "#94a3b8";
	}

	private comegoTypeToSegmentClass(type: ComegoTime["type"]): string | null {
		switch (type) {
			case "work":
				return "work";
			case "pause":
				return "break";
			case "absence":
				return "absence";
			default:
				return null;
		}
	}

	private getWorkinghourTypeSelectionStyle(typeClass: string): Record<string, string> | null {
		switch (typeClass) {
			case "work":
				return { backgroundColor: "#f9ef2a", color: "#e7dc0e" };
			case "break":
				return { backgroundColor: "#bdbdbd", color: "#616161" };
			case "absence":
				return { backgroundColor: "#9356e3", color: "white" };
			case "overtime":
				return { backgroundColor: "#63c730", color: "white" };
			default:
				return null;
		}
	}
}
